import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/utils/apiClient';
import { type FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';


interface DemoListItem {
  id: string;
  title: string;
  description: string;
  industry: string;
  icon: string;
  status: 'draft' | 'published' | 'archived';
  isStatic?: boolean;
  createdAt?: string;
  url: string;
}

interface ApiDemo {
  id: string;
  title: string;
  description: string;
  industry: string;
  businessDomain: string;
  status: 'draft' | 'in_progress' | 'published' | 'archived';
  createdAt: string;
  updatedAt: string;
  userId: string;
  publicUrl: string;
}

interface DemoListResponse {
  success: boolean;
  data: ApiDemo[];
  count: number;
}

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'published':
      return '#10b981'; // Green
    case 'draft':
      return '#f59e0b'; // Orange
    case 'in_progress':
      return '#3b82f6'; // Blue
    case 'archived':
      return '#6b7280'; // Gray
    default:
      return '#667eea'; // Blue
  }
};

// Helper function to get industry icon
const getIndustryIcon = (industry: string): string => {
  const industryLower = (industry || '').toLowerCase();

  if (industryLower.includes('automotive') || industryLower.includes('certification')) {
    return '🚗';
  }
  if (industryLower.includes('fragrance') || industryLower.includes('manufacturing')) {
    return '🌸';
  }
  if (industryLower.includes('healthcare') || industryLower.includes('medical')) {
    return '🏥';
  }
  if (industryLower.includes('finance') || industryLower.includes('banking')) {
    return '💰';
  }
  if (industryLower.includes('retail') || industryLower.includes('ecommerce')) {
    return '🛍️';
  }
  if (industryLower.includes('education') || industryLower.includes('learning')) {
    return '📚';
  }
  if (industryLower.includes('technology') || industryLower.includes('software')) {
    return '💻';
  }

  return '🤖'; // Default icon
};

const HomePage: FC = () => {
  const { user, signOut, isAuthenticated } = useAuth();
  const [demos, setDemos] = useState<DemoListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);





  // Delete demo function
  const deleteDemo = async (demoId: string, demoTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${demoTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/demo/${demoId}`);

      if (response.error) {
        console.error('Demo deletion error:', response.error);
        setError(`Failed to delete demo: ${response.error}`);
        return;
      }

      // Remove the demo from the local state
      setDemos(prevDemos => prevDemos.filter(demo => demo.id !== demoId));

      // Clear any existing error
      setError(null);

      console.log('Demo deleted successfully:', demoId);
    } catch (err) {
      console.error('Error deleting demo:', err);
      setError('Failed to delete demo');
    }
  };

  // Fetch demos from API
  const fetchDemos = async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setDemos([]); // Show empty list when not authenticated
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get<DemoListResponse>('/demo');

      console.log('Demo list API response:', response);

      if (response.error) {
        console.error('Demo list API error:', response.error);
        setError(response.error);
        setDemos([]); // Show empty list on error
        return;
      }

      // Process demos from the API response
      const allDemos: DemoListItem[] = [];

      console.log('Processing demo data:', response.data);

      // Check for successful response and data
      if (response.status >= 200 && response.status < 300 && Array.isArray(response.data)) {
        console.log('Found demos:', response.data.length);
        for (const demo of response.data) {
          console.log('Processing demo:', demo);
          // Map API demo to DemoListItem format
          allDemos.push({
            id: demo.id,
            title: demo.title,
            description: demo.description,
            industry: demo.industry || 'Custom',
            icon: getIndustryIcon(demo.industry || demo.businessDomain),
            status: demo.status as 'draft' | 'published' | 'archived',
            isStatic: false,
            createdAt: demo.createdAt,
            url: demo.publicUrl,
          });
        }
      } else {
        console.log('No demo data found in response or invalid response format');
      }

      console.log('Setting demos:', allDemos);
      setDemos(allDemos);
    } catch (err) {
      console.error('Error fetching demos:', err);
      setError('Failed to load demos');
      setDemos([]); // Show empty list on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDemos();
  }, [isAuthenticated]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Authentication Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '40px',
          color: 'white'
        }}>
          <div>
            <span style={{ opacity: 0.9 }}>Welcome, {user?.username || user?.email}</span>
          </div>
          <button
            onClick={signOut}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            Sign Out
          </button>
        </div>

        <header style={{ textAlign: 'center', color: 'white', marginBottom: '60px' }}>
          <h1 style={{ fontSize: '3em', marginBottom: '20px' }}>🤖 Demo Generator Portal</h1>
          <p style={{ fontSize: '1.3em', opacity: 0.9, marginBottom: '40px' }}>
            Multi-Agent AI Demonstration Platform
          </p>

          {/* Demo Creation Options */}
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Conversational Demo Creator - Temporarily disabled (under development) */}
            <button
              disabled
              style={{
                background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                border: 'none',
                color: 'white',
                padding: '20px 28px',
                borderRadius: '12px',
                cursor: 'not-allowed',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 8px 20px rgba(107, 114, 128, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
                textAlign: 'center',
                opacity: 0.6
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '4px' }}>🚧</div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>Create Demo</div>
              <div style={{ fontSize: '14px', opacity: 0.9, lineHeight: '1.4' }}>
                Under development - Coming soon!
              </div>
            </button>

            {/* Quick Demo Generator */}
            <Link
              to="/generate-demo"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                color: 'white',
                padding: '20px 28px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
                textAlign: 'center',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 25px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.3)';
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '4px' }}>⚡</div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>Quick Demo Generator</div>
              <div style={{ fontSize: '14px', opacity: 0.9, lineHeight: '1.4' }}>
                Generate demo from a single business use case prompt
              </div>
            </Link>
          </div>
        </header>



        {/* AgentCore Demo - Temporarily disabled (under development) */}
        {/* <div style={{ marginBottom: '40px' }}>
          <AgentCoreDemo />
        </div> */}

        {/* Loading State */}
        {isLoading && (
          <div style={{
            textAlign: 'center',
            color: 'white',
            fontSize: '1.2em',
            padding: '40px'
          }}>
            <div style={{ marginBottom: '20px' }}>🔄</div>
            Loading demos...
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div style={{
            textAlign: 'center',
            color: '#ff6b6b',
            background: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid rgba(255, 107, 107, 0.3)',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ marginBottom: '10px' }}>⚠️</div>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Error Loading Demos</div>
            <div style={{ fontSize: '0.9em', opacity: 0.8 }}>{error}</div>
          </div>
        )}

        {/* Demo Grid */}
        {!isLoading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '30px'
          }}>
            {demos.map((demo) => (
              <div
                key={demo.id}
                style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
                }}
              >
                {/* Status and Actions */}
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDemo(demo.id, demo.title);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ef4444';
                    }}
                    title={`Delete ${demo.title}`}
                  >
                    🗑️ Delete
                  </button>
                  <span style={{
                    background: getStatusColor(demo.status),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    {demo.status}
                  </span>
                </div>

                {/* Demo Content - Clickable Link */}
                <Link
                  to={demo.url}
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    color: 'inherit',
                  }}
                >
                  <div style={{ fontSize: '3em', marginBottom: '15px' }}>{demo.icon}</div>
                  <h2 style={{
                    color: '#2c3e50',
                    marginBottom: '10px',
                    fontSize: '1.5em',
                    paddingRight: '120px' // Make room for status indicator and delete button
                  }}>
                    {demo.title}
                  </h2>
                  <p style={{
                    color: '#666',
                    marginBottom: '15px',
                    lineHeight: '1.6'
                  }}>
                    {demo.description}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{
                      display: 'inline-block',
                      background: '#667eea',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                    }}>
                      {demo.industry}
                    </div>

                    {demo.createdAt && (
                      <div style={{
                        fontSize: '0.8em',
                        color: '#999',
                        fontStyle: 'italic'
                      }}>
                        Created {new Date(demo.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}

            {/* Empty State */}
            {demos.length === 0 && !error && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                color: 'white',
                padding: '60px 20px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '15px',
                border: '2px dashed rgba(255, 255, 255, 0.3)'
              }}>
                <div style={{ fontSize: '3em', marginBottom: '20px' }}>🎭</div>
                <h3 style={{ marginBottom: '10px' }}>No Demos Available</h3>
                <p style={{ opacity: 0.8, marginBottom: '30px' }}>
                  Create your first demo to get started with multi-agent AI demonstrations.
                </p>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link
                    to="/generate-demo"
                    style={{
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ⚡ Quick Generator
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        <footer style={{
          textAlign: 'center',
          color: 'white',
          marginTop: '80px',
          opacity: 0.8
        }}>
          <p>Built with React and powered by AWS Bedrock</p>
        </footer>
      </div>


    </div>
  );
};

export default HomePage;